import { useCallback, useEffect, useRef, useState } from "react";
import { type DocTypeListEntry, getDocTypes } from "../../api/client";
import { rememberPrivateTemplates, uiCodesForBackendDocTypes } from "../doc-catalog";

export interface UseAllowedDocCodesResult {
  /** Display ``code``s the signed-in user's tier may draft, for
   *  ``DoctypePicker``'s ``allowedDocCodes``. ``null`` only while the first
   *  fetch is in flight — the picker should treat null as "still loading".
   *  Once resolved it is a concrete list (empty on error = fail-closed). */
  allowedDocCodes: string[] | null;
  /** Private templates linked to ``workspaceId`` (``[]`` when none or no
   *  workspace). ``null`` while a fetch for the current workspace is in flight. */
  privateTemplates: DocTypeListEntry[] | null;
  /** Submission regions offered (``{code: label}``) — one shared list, read
   *  off the first entry that carries it. ``null`` until known. */
  regions: Record<string, string> | null;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

/**
 * Resolve which doctypes the doctype picker may show, straight from the
 * backend's tier-filtered ``GET /doc-types``.
 *
 * The server is the sole authority: it returns only the
 * ``doctypes/doc_pricing.json`` doctypes for a ``pay_per_use`` account and everything for
 * other tiers, so the add-in adds no allowlist or tier logic of its own — it
 * translates the returned backend codes to the picker's display codes
 * (:func:`uiCodesForBackendDocTypes`) and shows exactly that.
 *
 * With a ``workspaceId`` the same call also returns that workspace's private
 * templates (``private: true``, members only — the server decides). They are
 * kept apart from ``allowedDocCodes`` because they are not in the static
 * catalog, whose merge would silently drop them. Refetched whenever the
 * workspace changes.
 *
 * Fail-closed: before the first fetch resolves ``allowedDocCodes`` is ``null``
 * (loading — the picker shows nothing draftable yet, never the full catalog),
 * and a failed fetch settles to ``[]`` rather than a baked-in fallback set.
 */
export function useAllowedDocCodes(workspaceId: string | null = null): UseAllowedDocCodesResult {
  const [allowedDocCodes, setAllowedDocCodes] = useState<string[] | null>(null);
  const [privateTemplates, setPrivateTemplates] = useState<DocTypeListEntry[] | null>(null);
  const [regions, setRegions] = useState<Record<string, string> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Latest-request-wins: a slow answer for the workspace the writer just left
  // must not overwrite the one they switched to.
  const requestSeq = useRef(0);

  const refresh = useCallback(async () => {
    const seq = ++requestSeq.current;
    setLoading(true);
    setError(null);
    setPrivateTemplates(null);
    try {
      const entries = await getDocTypes(workspaceId);
      if (seq !== requestSeq.current) return;
      const publicEntries = entries.filter((e) => !e.private);
      const templates = entries.filter((e) => e.private && (!workspaceId || e.workspace_id === workspaceId));
      rememberPrivateTemplates(templates);
      setAllowedDocCodes(uiCodesForBackendDocTypes(publicEntries.map((e) => e.code)));
      setPrivateTemplates(templates);
      const withRegions = entries.find((e) => e.regions && Object.keys(e.regions).length > 0);
      if (withRegions?.regions) setRegions(withRegions.regions);
    } catch (err) {
      if (seq !== requestSeq.current) return;
      const message = err instanceof Error ? err.message : String(err);
      console.warn("[useAllowedDocCodes] getDocTypes failed:", err);
      setError(message);
      // Fail-closed: an unreadable catalogue narrows the picker to nothing
      // rather than falling open to every doctype.
      setAllowedDocCodes([]);
      setPrivateTemplates([]);
    } finally {
      if (seq === requestSeq.current) setLoading(false);
    }
  }, [workspaceId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return { allowedDocCodes, privateTemplates, regions, loading, error, refresh };
}
